import React, { useState, useEffect } from 'react';
import DatePicker from 'components/Inputs/DatePicker.js';
import { Col, Input, InputGroup } from 'reactstrap';
import { ErrorMessage } from '@hookform/error-message';

import FlexSelect from 'components/Inputs/FlexSelect';

import { customFieldTypes } from 'views/fieldConfigs/utils/customFieldTypes';
import CurrencyMask from 'components/Inputs/CurrencyMask';

import CPFMask from 'components/Inputs/CPFMask';
import CNPJMask from 'components/Inputs/CNPJMask';
import { removeMaskSpecialCharacters } from '../../utils/phoneNumberHelpers';
import TemplatesModal from 'views/ServicePanel/components/QueueContent/components/Contact/TemplatesModal';
import { useCustomFields } from 'views/Protocol/form/context/CustomFieldsContext';
import { FlexController } from 'components/FlexControllerInput';
import { ActionInputButton } from 'components/FlexControllerInput/components/styles';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useSelector } from 'react-redux';

const CustomFieldsInputsForm = ({
  fields,
  control,
  errors,
  watch,
  setValue,
  valueFieldsReferenceLoading,
  Controller,
  setChatCreatedOnIsolatedMessageId,
  isTryingToCreateAContactAttempt = false,
  isUpdate = false,
  isOnEditPage = false,
  options = {
    showCallIconOnPhoneField: false,
    showMessageIconOnPhoneField: false,
    submitAttempt: false
  },
  contactMessageClientName,
  draggableOptions = {
    itCanBeDragged: false,
    itIsDraggable: false
  },
  tabIndex = 0
}) => {
  const { changeFieldsReference, getValueFieldsReference } = useCustomFields();
  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const [
    isModalTemplateMessageCustomFieldsOpen,
    setIsisModalTemplateMessageCustomFieldsOpen
  ] = useState(false);
  const [
    whatsAppMessageContactInfoCustomFields,
    setWhatsAppMessageContactInfoCustomFields
  ] = useState({});

  useEffect(() => {
    fields.forEach((field) => {
      const value = watch(field.name);
      if (value) {
        verifyParentId(field, value);
        setValue(field.name, value);
      }
    });
  }, [JSON.stringify(fields)]);

  const errorFormMessage = (message) => (
    <p style={{ color: 'red', marginBottom: '0' }}>{message}</p>
  );

  function handleWhatsAppMessage(phone) {
    let ddi = '';
    let number = '';

    if (typeof phone === 'object' && phone !== null) {
      ddi = phone.ddi || '';
      number = phone.number || '';
    } else if (typeof phone === 'string') {
      number = phone;
    }

    const phoneNumberWithoutMask = removeMaskSpecialCharacters(number);
    let data = {
      nome: contactMessageClientName ?? '',
      telefone: phoneNumberWithoutMask !== '' ? phoneNumberWithoutMask : '',
      ddi: ddi
    };

    setWhatsAppMessageContactInfoCustomFields(data);
    setIsisModalTemplateMessageCustomFieldsOpen(
      !isModalTemplateMessageCustomFieldsOpen
    );
    return whatsAppMessageContactInfoCustomFields;
  }

  function handleOpenUrlCustomField(url) {
    if (!url) return;
    const urlToOpen =
      url.startsWith('http://') || url.startsWith('https://')
        ? url.replace('http://', '').replace('https://', '')
        : url.replace(/.*\/\//, '');
    window.open(`https://${urlToOpen}`, '_blank');
  }

  const verifyParentId = (field, value) => {
    if (!field?.isReference && field?.parentName) {
      (field?.options ?? []).map((option) => {
        if (option.value === value) {
          changeFieldsReference({
            ...getValueFieldsReference(),
            [field?.parentName]: option.parentsValue.map((value) => ({
              value,
              label: value
            }))
          });
        }
      });

      return;
    }
  };

  const filterFields = (fields) => {
    if (isOnEditPage) return fields;

    const isOnlyToShowOnEdit = fields.filter(
      (field) => !field.recordTypesConfig.isOnlyToShowOnEdit
    );

    return isOnlyToShowOnEdit;
  };

  return (
    <>
      {!valueFieldsReferenceLoading &&
        filterFields(fields)?.map((field) => {
          if (field.type === customFieldTypes.TEXT) {
            return (
              <Col
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '6'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => (
                    <Input
                      tabIndex={tabIndex}
                      value={props.value}
                      onChange={props.onChange}
                      type="text"
                      placeholder={field.label}
                      disabled={
                        (isUpdate && field.recordTypesConfig.isNotEditable) ||
                        field.blocked
                      }
                    />
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.DATE) {
            return (
              <Col
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '6'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => (
                    <DatePicker
                      tabIndex={tabIndex}
                      fieldName={field.name}
                      valueController={setValue}
                      defaultValue={props.value}
                      shouldDisable={
                        (isUpdate && field.recordTypesConfig.isNotEditable) ||
                        field.blocked
                      }
                    />
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.LIST) {
            return (
              <Col
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '6'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => (
                    <FlexSelect
                      tabIndex={tabIndex}
                      dataOptions={
                        !field?.isReference
                          ? field.options.map((option) => ({
                              label: option.value,
                              value: option.value
                            }))
                          : getValueFieldsReference()[field.name] || []
                      }
                      isMulti={false}
                      isClearable={true}
                      value={props.value || ''}
                      valueController={(fieldName, fieldValue) => {
                        setValue(fieldName, fieldValue);
                        verifyParentId(field, fieldValue);
                      }}
                      closeMenuOnSelect={true}
                      fieldName={field.name}
                      labelName="label"
                      valueName="value"
                      isDisabled={
                        (field.isReference && !watch(field.parentName)) ||
                        (isUpdate && field.recordTypesConfig.isNotEditable) ||
                        field.blocked
                      }
                      portalTarget={true}
                    />
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.MULTIPLE) {
            return (
              <Col
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '6'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => {
                    return (
                      <FlexSelect
                        tabIndex={tabIndex}
                        dataOptions={field?.options.map((option) => ({
                          label: option.value,
                          value: option.value
                        }))}
                        isMulti={true}
                        isClearable={true}
                        value={props.value || ''}
                        valueController={setValue}
                        defaultValue=""
                        closeMenuOnSelect={false}
                        fieldName={field.name}
                        labelName="label"
                        valueName="value"
                        isDisabled={
                          (isUpdate && field.recordTypesConfig.isNotEditable) ||
                          field.blocked
                        }
                        maxHeight={'65px'}
                        portalTarget={true}
                      />
                    );
                  }}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.NUMERIC) {
            return (
              <Col
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '6'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => (
                    <Input
                      tabIndex={tabIndex}
                      value={props.value}
                      onChange={props.onChange}
                      type="number"
                      placeholder={field.label}
                      disabled={
                        (isUpdate && field.recordTypesConfig.isNotEditable) ||
                        field.blocked
                      }
                    />
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.CURRENCY) {
            return (
              <Col
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '6'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => (
                    <CurrencyMask
                      tabIndex={tabIndex}
                      valueController={setValue}
                      value={props.value}
                      fieldName={field.name}
                      shouldDisable={
                        (isUpdate && field.recordTypesConfig.isNotEditable) ||
                        field.blocked
                      }
                    />
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.HOUR) {
            return (
              <Col
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '6'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => (
                    <Input
                      tabIndex={tabIndex}
                      value={props.value}
                      onChange={props.onChange}
                      type="time"
                      step="2"
                      placeholder={field.label}
                      disabled={
                        (isUpdate && field.recordTypesConfig.isNotEditable) ||
                        field.blocked
                      }
                    />
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.TELEPHONE) {
            return (
              <>
                <Col
                  className={
                    draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                  }
                  sm={draggableOptions.itCanBeDragged ? '' : '6'}
                  key={field.id}
                >
                  <FlexController
                    type="phone"
                    label={field.label}
                    name={field.name}
                    disabled={false}
                    showMessageButton={options?.showMessageIconOnPhoneField}
                    showCallButton={options?.showCallIconOnPhoneField}
                    handleSendTemplate={handleWhatsAppMessage}
                    watch={watch}
                    value={watch(field.name)}
                    control={control}
                    errors={errors}
                    rules={{
                      required:
                        field?.recordTypesConfig?.isRequired || field?.required
                          ? 'Campo obrigatório'
                          : false
                    }}
                    required={
                      field.required || field.recordTypesConfig.isRequired
                    }
                    validateOnSubmit={options?.submitAttempt}
                    valueController={(fieldName, value) => {
                      setValue(fieldName, value);
                    }}
                  />
                </Col>
                <TemplatesModal
                  isModalOpen={isModalTemplateMessageCustomFieldsOpen}
                  onModalToggle={setIsisModalTemplateMessageCustomFieldsOpen}
                  clientNumber={whatsAppMessageContactInfoCustomFields.telefone}
                  clientDDI={whatsAppMessageContactInfoCustomFields.ddi}
                  clientName={whatsAppMessageContactInfoCustomFields.nome}
                  clientId={
                    whatsAppMessageContactInfoCustomFields.clientId ?? ''
                  }
                  setChatCreatedOnIsolatedMessageId={
                    setChatCreatedOnIsolatedMessageId
                  }
                  isTryingToCreateAContactAttempt={
                    isTryingToCreateAContactAttempt
                  }
                  isIsolated={true}
                />
              </>
            );
          }

          if (field.type === customFieldTypes.CPF) {
            return (
              <Col
                md={draggableOptions.itCanBeDragged ? '' : '6'}
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '12'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => (
                    <CPFMask
                      tabIndex={tabIndex}
                      valueController={setValue}
                      fieldName={field.name}
                      value={props.value}
                      placeholder="000.000.000-00"
                      shouldDisable={
                        (isUpdate && field.recordTypesConfig.isNotEditable) ||
                        field.blocked
                      }
                    />
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false,
                    validate: (value) => {
                      if (!value) return true;
                      let cpfFormated = value.replace(/\D/g, '');
                      if (!cpfFormated) return true;
                      if (cpfFormated.length !== 11) return 'CPF inválido';
                      return true;
                    }
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.CNPJ) {
            return (
              <Col
                md={draggableOptions.itCanBeDragged ? '' : '6'}
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '12'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => (
                    <CNPJMask
                      tabIndex={tabIndex}
                      valueController={setValue}
                      fieldName={field.name}
                      value={props.value}
                      placeholder="00.000.000/0001-00"
                      shouldDisable={
                        (isUpdate && field.recordTypesConfig.isNotEditable) ||
                        field.blocked
                      }
                    />
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false,
                    validate: (value) => {
                      if (!value) return true;
                      let cnpjFormated = value.replace(/\D/g, '');
                      if (!cnpjFormated) return true;
                      if (cnpjFormated.length !== 14) return 'CNPJ inválido';
                      return true;
                    }
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.LINK) {
            return (
              <Col
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '6'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired ? (
                    <span style={{ color: 'red' }}>*</span>
                  ) : (
                    ''
                  )}
                </label>
                <Controller
                  render={(props) => (
                    <>
                      <InputGroup>
                        <Input
                          tabIndex={tabIndex}
                          value={props.value}
                          onChange={props.onChange}
                          type="text"
                          placeholder={'https://'}
                          disabled={
                            isUpdate && field.recordTypesConfig.isNotEditable
                          }
                          margin="20px"
                        />
                        <ActionInputButton
                          primaryColor={systemConfiguration.primary_color}
                          onClick={() => handleOpenUrlCustomField(props.value)}
                        >
                          <FaExternalLinkAlt size={12} />
                        </ActionInputButton>
                      </InputGroup>
                    </>
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required: field.recordTypesConfig.isRequired
                      ? 'Campo obrigatório'
                      : false
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }

          if (field.type === customFieldTypes.TEXTAREA) {
            return (
              <Col
                className={
                  draggableOptions.itCanBeDragged ? 'mb-0 p-0' : 'mb-3'
                }
                sm={draggableOptions.itCanBeDragged ? '' : '6'}
                key={field.id}
              >
                <label
                  className="form-control-label"
                  style={{
                    cursor: draggableOptions.itIsDraggable ? 'grab' : 'default'
                  }}
                >
                  {field.label}
                  {field.recordTypesConfig.isRequired || field.required
                    ? '*'
                    : ''}
                </label>
                <Controller
                  render={(props) => (
                    <Input
                      type="textarea"
                      tabIndex={tabIndex}
                      value={props.value}
                      onChange={props.onChange}
                      placeholder={field.label}
                      disabled={
                        (isUpdate && field.recordTypesConfig.isNotEditable) ||
                        field.blocked
                      }
                      style={{ minHeight: '7.5rem', resize: 'vertical' }}
                    />
                  )}
                  control={control}
                  defaultValue=""
                  rules={{
                    required:
                      field.required ||
                      field.recordTypesConfig.isRequired ||
                      field.required
                        ? 'Campo obrigatório'
                        : false
                  }}
                  name={field.name}
                />
                <ErrorMessage
                  errors={errors}
                  name={field.name}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            );
          }
        })}
    </>
  );
};
export default CustomFieldsInputsForm;
